import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Lock, Play, Plus, Square, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import SearchableSelect from '@/components/common/SearchableSelect';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useClientScope } from '@/context/ClientScopeProvider';
import { apiError } from '@/lib/api-error';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { hoursLabel, type TimeEntry } from '@/types/advisor-billing';
import { money, type Offer } from '@/types/offer';

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_PX = 44;
const DEFAULT_FIRST_HOUR = 6;
const DEFAULT_LAST_HOUR = 22;

type EntryDraft = { id?: string; date: string; start: string; hours: string; description: string; rate: string };

function draftFrom(entry: TimeEntry): EntryDraft {
  const s = new Date(entry.started_at);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    id: entry.id,
    date: `${s.getFullYear()}-${pad(s.getMonth() + 1)}-${pad(s.getDate())}`,
    start: `${pad(s.getHours())}:${pad(s.getMinutes())}`,
    hours: entry.hours,
    description: entry.description,
    rate: entry.rate ?? '',
  };
}

function draftAt(date: Date, hour: number, rate: string): EntryDraft {
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    start: `${pad(hour)}:00`,
    hours: '1',
    description: '',
    rate,
  };
}

function timesOf(draft: EntryDraft) {
  const started = new Date(`${draft.date}T${draft.start || '09:00'}`);
  const hours = parseFloat(draft.hours);
  const ended = new Date(started.getTime() + (Number.isFinite(hours) ? hours : 0) * 3600000);
  return { started, ended, hours };
}

function startOfWeek(day: Date) {
  const d = new Date(day);
  d.setHours(0, 0, 0, 0);
  const offset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - offset);
  return d;
}

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

export default function ClientTimePage() {
  const { clientId, client } = useClientScope();
  const queryClient = useQueryClient();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [description, setDescription] = useState('');
  const [rateId, setRateId] = useState('');
  const [editing, setEditing] = useState<TimeEntry | null>(null);
  const [adding, setAdding] = useState<null | EntryDraft>(null);

  const weekEnd = new Date(weekStart.getTime() + 6 * DAY_MS);

  const { data: contracts } = useQuery<Offer[]>({
    queryKey: ['client-contracts-running', clientId],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/advisor/offers/', { params: { client: clientId, status: 'accepted' } });
      return ((res.data?.results ?? res.data ?? []) as Offer[]).filter((o) => o.is_running);
    },
  });
  const contract = contracts?.[0];
  const rates = useMemo(() => {
    if (!contract) return [];
    return contract.packages
      .filter((p) => p.id === contract.accepted_package)
      .flatMap((p) => p.hourly_rates);
  }, [contract]);

  const { data: entries, isLoading } = useQuery<TimeEntry[]>({
    queryKey: ['time-entries', clientId, isoDate(weekStart)],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/advisor/time-entries/', {
        params: { client: clientId, from: isoDate(weekStart), to: isoDate(weekEnd) },
      });
      return res.data?.results ?? res.data ?? [];
    },
    refetchInterval: 30000,
  });

  const { data: running } = useQuery<TimeEntry | null>({
    queryKey: ['time-entry-running'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/advisor/time-entries/', { params: { running: 'true' } });
      const rows = (res.data?.results ?? res.data ?? []) as TimeEntry[];
      return rows[0] ?? null;
    },
    refetchInterval: 15000,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['time-entries', clientId] });
    queryClient.invalidateQueries({ queryKey: ['time-entry-running'] });
  };

  const start = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post('/advisor/time-entries/start/', {
        client: clientId, offer: contract?.id ?? null, rate: rateId || null, description,
      });
    },
    onSuccess: () => { setDescription(''); refresh(); },
    onError: (e) => toast.error(apiError(e, 'Could not start the timer.')),
  });

  const stop = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post(`/advisor/time-entries/${id}/stop/`);
    },
    onSuccess: refresh,
    onError: (e) => toast.error(apiError(e, 'Could not stop the timer.')),
  });

  const save = useMutation({
    mutationFn: async (entry: EntryDraft) => {
      const { started, ended, hours } = timesOf(entry);
      if (!Number.isFinite(hours) || hours <= 0) throw new Error('Say how many hours were worked.');
      const api = await authenticatedBitswanBackendInstance();
      const body = {
        client: clientId, offer: contract?.id ?? null,
        rate: entry.rate || null, description: entry.description,
        started_at: started.toISOString(),
        ended_at: ended.toISOString(),
      };
      return entry.id
        ? api.patch(`/advisor/time-entries/${entry.id}/`, body)
        : api.post('/advisor/time-entries/', body);
    },
    onSuccess: () => { setEditing(null); setAdding(null); refresh(); },
    onError: (e) => toast.error(e instanceof Error && !('response' in e) ? e.message : apiError(e, 'Could not save the entry.')),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.delete(`/advisor/time-entries/${id}/`);
    },
    onSuccess: () => { setEditing(null); refresh(); },
    onError: (e) => toast.error(apiError(e, 'Could not delete the entry.')),
  });

  const days = Array.from({ length: 7 }, (_, i) => new Date(weekStart.getTime() + i * DAY_MS));
  const firstHour = Math.min(DEFAULT_FIRST_HOUR, ...(entries ?? []).map((e) => new Date(e.started_at).getHours()));
  const lastHour = Math.max(DEFAULT_LAST_HOUR, ...(entries ?? []).map((e) => {
    const end = e.ended_at ? new Date(e.ended_at) : new Date();
    return Math.min(24, end.getHours() + (end.getMinutes() > 0 ? 1 : 0));
  }));
  const gridHeight = (lastHour - firstHour) * HOUR_PX;
  const totalHours = (entries ?? []).reduce((s, e) => s + parseFloat(e.hours || '0'), 0);
  const totalAmount = (entries ?? []).reduce((s, e) => s + parseFloat(e.amount || '0'), 0);

  return (
    <div className="space-y-4" data-testid="client-time-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Time</h2>
          <p className="text-muted-foreground">
            Hours you work for {client.company_name}, at the rates in their contract.
          </p>
        </div>
      </div>

      {!contract && (
        <p className="rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground" data-testid="time-no-contract">
          There is no running contract with {client.company_name}, so hours logged here have no rate yet. You can still track them; they are billed once a contract is accepted.
        </p>
      )}

      <Card data-testid="timer-bar">
        <CardContent className="flex flex-wrap items-center gap-2 pt-4">
          {running && running.client === clientId ? (
            <>
              <span className="flex-1 truncate text-sm">
                <span className="mr-2 inline-block h-2 w-2 animate-pulse rounded-full bg-red-500" />
                {running.description || 'Working'} · since {new Date(running.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {running.rate_label && <span className="text-muted-foreground"> · {running.rate_label}</span>}
              </span>
              <Button variant="destructive" size="sm" onClick={() => stop.mutate(running.id)} data-testid="timer-stop">
                <Square className="mr-2 h-3.5 w-3.5" /> Stop
              </Button>
            </>
          ) : (
            <>
              <Input
                className="min-w-[200px] flex-1"
                placeholder="What are you working on?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && start.mutate()}
                data-testid="timer-description"
              />
              <SearchableSelect
                className="w-56"
                value={rateId}
                onChange={setRateId}
                placeholder={rates.length ? 'Rate…' : 'No rates'}
                searchPlaceholder="Search rates…"
                options={rates.map((r) => ({ value: r.id, label: r.label, hint: `${money(r.hourly_rate, contract!.currency)}/h` }))}
                testId="timer-rate"
              />
              <Button onClick={() => start.mutate()} disabled={start.isPending} data-testid="timer-start">
                <Play className="mr-2 h-3.5 w-3.5" /> Start
              </Button>
              {running && (
                <span className="text-xs text-muted-foreground">A timer is running for {running.company_name}; starting here stops it.</span>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setWeekStart(new Date(weekStart.getTime() - 7 * DAY_MS))} data-testid="week-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek(new Date()))}>Today</Button>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(new Date(weekStart.getTime() + 7 * DAY_MS))} data-testid="week-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="ml-2 text-sm font-medium">
            {weekStart.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} – {weekEnd.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span data-testid="week-total">{hoursLabel(totalHours)} this week{contract && totalAmount > 0 ? ` · ${money(totalAmount, contract.currency)}` : ''}</span>
          <Button size="sm" variant="outline" onClick={() => setAdding(draftAt(new Date(), 9, rateId))} data-testid="add-entry-btn">
            <Plus className="mr-2 h-3.5 w-3.5" /> Add time
          </Button>
        </div>
      </div>

      {(adding || editing) && (
        <EntryForm
          key={adding ? 'new' : editing!.id}
          value={adding ?? draftFrom(editing!)}
          rates={rates}
          currency={contract?.currency ?? 'EUR'}
          locked={Boolean(editing?.is_locked)}
          onCancel={() => { setAdding(null); setEditing(null); }}
          onSave={(v) => save.mutate(v)}
          onDelete={editing && !editing.is_locked ? () => remove.mutate(editing.id) : undefined}
          saving={save.isPending}
        />
      )}

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <div className="grid min-w-[760px]" style={{ gridTemplateColumns: '48px repeat(7, 1fr)' }} data-testid="week-grid">
            <div />
            {days.map((d) => (
              <div key={d.toISOString()} className="border-b border-l px-2 py-2 text-center text-xs font-medium">
                {d.toLocaleDateString(undefined, { weekday: 'short' })}<br />
                <span className={isoDate(d) === isoDate(new Date()) ? 'text-primary' : 'text-muted-foreground'}>{d.getDate()}</span>
              </div>
            ))}
            <div className="relative" style={{ height: gridHeight }}>
              {Array.from({ length: lastHour - firstHour }, (_, i) => (
                <div key={i} className="absolute right-1 text-[10px] text-muted-foreground" style={{ top: i * HOUR_PX - 6 }}>
                  {String(firstHour + i).padStart(2, '0')}:00
                </div>
              ))}
            </div>
            {days.map((d) => {
              const dayEntries = (entries ?? []).filter((e) => isoDate(new Date(e.started_at)) === isoDate(d));
              return (
                <div
                  key={d.toISOString()}
                  className="relative cursor-cell border-l"
                  style={{ height: gridHeight }}
                  data-testid={`day-${isoDate(d)}`}
                  onClick={(event) => {
                    if (event.target !== event.currentTarget) return;
                    const y = event.clientY - event.currentTarget.getBoundingClientRect().top;
                    setEditing(null);
                    setAdding(draftAt(d, firstHour + Math.floor(y / HOUR_PX), rateId));
                  }}
                >
                  {Array.from({ length: lastHour - firstHour }, (_, i) => (
                    <div key={i} className="pointer-events-none absolute inset-x-0 border-t border-dashed border-muted" style={{ top: i * HOUR_PX }} />
                  ))}
                  {dayEntries.map((e) => {
                    const s = new Date(e.started_at);
                    const en = e.ended_at ? new Date(e.ended_at) : new Date();
                    const top = ((s.getHours() + s.getMinutes() / 60) - firstHour) * HOUR_PX;
                    const height = Math.max(18, ((en.getTime() - s.getTime()) / 3600000) * HOUR_PX);
                    return (
                      <button
                        key={e.id}
                        type="button"
                        className={`absolute inset-x-1 overflow-hidden rounded-md border px-1.5 py-0.5 text-left text-[11px] leading-tight ${e.is_locked ? 'border-muted bg-muted/60 text-muted-foreground' : e.is_running ? 'border-red-300 bg-red-500/10' : 'border-primary/30 bg-primary/10 hover:bg-primary/20'}`}
                        style={{ top: Math.max(0, top), height }}
                        onClick={() => !e.is_running && setEditing(e)}
                        data-testid={`time-entry-${e.id}`}
                        title={e.description}
                      >
                        <span className="font-medium">{e.description || 'Work'}</span>
                        <span className="block text-muted-foreground">
                          {hoursLabel(e.hours)}{e.rate_label ? ` · ${e.rate_label}` : ''}
                          {e.is_locked && <Lock className="ml-1 inline h-2.5 w-2.5" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {isLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
    </div>
  );
}

function EntryForm({
  value, rates, currency, locked, onCancel, onSave, onDelete, saving,
}: {
  value: EntryDraft;
  rates: { id?: string; label: string; hourly_rate: string }[];
  currency: string;
  locked: boolean;
  onCancel: () => void;
  onSave: (v: EntryDraft) => void;
  onDelete?: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState(value);
  const { ended, hours } = timesOf(form);
  const rate = rates.find((r) => r.id === form.rate);
  const pad = (n: number) => String(n).padStart(2, '0');
  const endValue = Number.isFinite(hours) && hours > 0 && form.start ? `${pad(ended.getHours())}:${pad(ended.getMinutes())}` : '';
  const setEnd = (end: string) => {
    if (!end || !form.start) return;
    const [sh, sm] = form.start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let minutes = (eh * 60 + em) - (sh * 60 + sm);
    if (minutes <= 0) minutes += 24 * 60;
    setForm({ ...form, hours: String(Math.round((minutes / 60) * 100) / 100) });
  };
  const valid = Number.isFinite(hours) && hours > 0 && Boolean(form.date) && Boolean(form.start);
  return (
    <Card data-testid="entry-form">
      <CardContent className="space-y-3 pt-4">
        {locked && (
          <Badge variant="outline"><Lock className="mr-1 h-3 w-3" /> On an invoice — read only</Badge>
        )}
        <Input placeholder="What was the work?" value={form.description} disabled={locked}
          onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="entry-description" />
        <div className="grid gap-2 sm:grid-cols-[160px_110px_110px_100px_1fr]">
          <label className="text-xs text-muted-foreground">Day
            <Input type="date" value={form.date} disabled={locked}
              onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="entry-date" />
          </label>
          <label className="text-xs text-muted-foreground">Started
            <Input type="time" value={form.start} disabled={locked}
              onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="entry-start-time" />
          </label>
          <label className="text-xs text-muted-foreground">Ended
            <Input type="time" value={endValue} disabled={locked}
              onChange={(e) => setEnd(e.target.value)} data-testid="entry-end-time" />
          </label>
          <label className="text-xs text-muted-foreground">Hours
            <Input type="number" min="0.25" step="0.25" inputMode="decimal" value={form.hours} disabled={locked}
              onChange={(e) => setForm({ ...form, hours: e.target.value })} data-testid="entry-hours" />
          </label>
          <label className="text-xs text-muted-foreground">Rate
            <SearchableSelect
              value={form.rate}
              onChange={(value) => setForm({ ...form, rate: value })}
              disabled={locked}
              placeholder="No rate"
              searchPlaceholder="Search rates…"
              options={[{ value: '', label: 'No rate' }, ...rates.map((r) => ({ value: r.id, label: r.label, hint: `${money(r.hourly_rate, currency)}/h` }))]}
              testId="entry-rate"
            />
          </label>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="entry-summary">
          {valid
            ? `${hoursLabel(hours)} · ${form.start}–${String(ended.getHours()).padStart(2, '0')}:${String(ended.getMinutes()).padStart(2, '0')}${rate ? ` · ${money(hours * parseFloat(rate.hourly_rate), currency)}` : ''}`
            : 'Enter how many hours were worked.'}
        </p>
        <div className="flex gap-2">
          {!locked && (
            <Button size="sm" onClick={() => onSave(form)} disabled={saving || !valid} data-testid="entry-save">Save</Button>
          )}
          <Button size="sm" variant="ghost" onClick={onCancel}>Close</Button>
          {onDelete && (
            <Button size="sm" variant="ghost" className="ml-auto text-destructive" onClick={onDelete} data-testid="entry-delete">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
